// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/safeZip.js
//
// Brush packs are untrusted. This reader parses the ZIP central directory
// itself so that every limit is checked before any byte is inflated:
// entry count, per-entry and total decompressed size, compression ratio,
// path traversal, encryption and ZIP64. Entries inflate lazily, so a pack
// can be inspected without decompressing textures nobody selected.
import { inflateSync } from 'fflate';

export const DEFAULT_ZIP_LIMITS = Object.freeze({
  maxEntries: 5000,
  maxEntryBytes: 96 * 1024 * 1024,
  maxTotalBytes: 768 * 1024 * 1024,
  // Deflate tops out near 1032:1. Real brush textures stay far below 200:1;
  // anything above this on a non-trivial entry is treated as a bomb.
  maxRatio: 250,
  ratioCheckMinBytes: 1024 * 1024,
  maxPathLength: 1024,
});

export class BrushArchiveError extends Error {
  constructor(message, code = 'archive_invalid') {
    super(message);
    this.name = 'BrushArchiveError';
    this.code = code;
  }
}

function readU16(bytes, offset) {
  return bytes[offset] | (bytes[offset + 1] << 8);
}

function readU32(bytes, offset) {
  return (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24)) >>> 0;
}

export function isZipBytes(bytes) {
  return !!bytes && bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b
    && ((bytes[2] === 3 && bytes[3] === 4) || (bytes[2] === 5 && bytes[3] === 6));
}

function decodeName(bytes, utf8) {
  if (utf8 && typeof TextDecoder !== 'undefined') {
    try {
      return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
    } catch {}
  }
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) out += String.fromCharCode(bytes[i]);
  return out;
}

// Returns a normalised relative path, or '' when the name must be rejected.
export function sanitiseArchivePath(rawName, maxLength = DEFAULT_ZIP_LIMITS.maxPathLength) {
  const name = String(rawName == null ? '' : rawName);
  if (!name || name.length > maxLength) return '';
  if (name.indexOf('\0') !== -1) return '';
  const forward = name.replace(/\\/g, '/');
  if (/^[a-zA-Z]:/.test(forward) || forward.startsWith('/') || forward.startsWith('//')) return '';
  const parts = [];
  for (const part of forward.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') return '';
    parts.push(part);
  }
  return parts.join('/') + (forward.endsWith('/') && parts.length ? '/' : '');
}

function findEndOfCentralDirectory(bytes) {
  const minimum = Math.max(0, bytes.length - 65557);
  for (let offset = bytes.length - 22; offset >= minimum; offset -= 1) {
    if (bytes[offset] === 0x50 && bytes[offset + 1] === 0x4b && bytes[offset + 2] === 5 && bytes[offset + 3] === 6) return offset;
  }
  return -1;
}

export function openZip(input, limits = {}) {
  const settings = { ...DEFAULT_ZIP_LIMITS, ...limits };
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || new ArrayBuffer(0));
  const eocd = findEndOfCentralDirectory(bytes);
  if (eocd < 0) throw new BrushArchiveError('Not a ZIP archive or the archive is truncated.', 'archive_truncated');

  const totalEntries = readU16(bytes, eocd + 10);
  const directorySize = readU32(bytes, eocd + 12);
  const directoryOffset = readU32(bytes, eocd + 16);
  if (totalEntries === 0xffff || directoryOffset === 0xffffffff) {
    throw new BrushArchiveError('ZIP64 archives are not supported for brush packs.', 'archive_zip64');
  }
  if (totalEntries > settings.maxEntries) {
    throw new BrushArchiveError(`Archive has ${totalEntries} entries; the limit is ${settings.maxEntries}.`, 'archive_too_many_entries');
  }
  if (directoryOffset + directorySize > bytes.length) {
    throw new BrushArchiveError('ZIP central directory points past the end of the file.', 'archive_truncated');
  }

  const entries = [];
  const seen = new Set();
  let declaredTotal = 0;
  let offset = directoryOffset;

  for (let index = 0; index < totalEntries; index += 1) {
    if (offset + 46 > bytes.length || readU32(bytes, offset) !== 0x02014b50) {
      throw new BrushArchiveError('Corrupted ZIP central directory.', 'archive_corrupt');
    }
    const flags = readU16(bytes, offset + 8);
    const method = readU16(bytes, offset + 10);
    const compressedSize = readU32(bytes, offset + 20);
    const size = readU32(bytes, offset + 24);
    const nameLength = readU16(bytes, offset + 28);
    const extraLength = readU16(bytes, offset + 30);
    const commentLength = readU16(bytes, offset + 32);
    const localOffset = readU32(bytes, offset + 42);
    const rawName = decodeName(bytes.subarray(offset + 46, offset + 46 + nameLength), (flags & 0x800) !== 0);
    offset += 46 + nameLength + extraLength + commentLength;

    const path = sanitiseArchivePath(rawName, settings.maxPathLength);
    const isDirectory = rawName.endsWith('/') || rawName.endsWith('\\');
    const problem = !path
      ? 'unsafe path'
      : (flags & 1)
        ? 'encrypted'
        : compressedSize === 0xffffffff || size === 0xffffffff || localOffset === 0xffffffff
          ? 'zip64'
          : method !== 0 && method !== 8
            ? 'unsupported compression ' + method
            : size > settings.maxEntryBytes
              ? 'entry too large'
              : size >= settings.ratioCheckMinBytes && compressedSize > 0 && size / compressedSize > settings.maxRatio
                ? 'suspicious compression ratio'
                : '';

    if (!problem && !isDirectory) {
      declaredTotal += size;
      if (declaredTotal > settings.maxTotalBytes) {
        throw new BrushArchiveError('Archive would decompress past the size limit.', 'archive_too_large');
      }
    }

    const key = path.toLowerCase();
    if (!isDirectory && path && seen.has(key)) continue;
    if (path) seen.add(key);

    entries.push({
      path,
      rawName,
      isDirectory,
      method,
      size,
      compressedSize,
      localOffset,
      rejected: problem,
    });
  }

  let inflatedTotal = 0;

  function readEntry(entry) {
    if (!entry || entry.isDirectory) throw new BrushArchiveError('Not a file entry.', 'archive_entry');
    if (entry.rejected) throw new BrushArchiveError(`${entry.rawName || 'entry'}: ${entry.rejected}`, 'archive_entry_rejected');
    const local = entry.localOffset;
    if (local + 30 > bytes.length || readU32(bytes, local) !== 0x04034b50) {
      throw new BrushArchiveError(`${entry.path}: corrupted local header.`, 'archive_corrupt');
    }
    const start = local + 30 + readU16(bytes, local + 26) + readU16(bytes, local + 28);
    const end = start + entry.compressedSize;
    if (end > bytes.length) throw new BrushArchiveError(`${entry.path}: data extends past the archive.`, 'archive_truncated');
    inflatedTotal += entry.size;
    if (inflatedTotal > settings.maxTotalBytes) throw new BrushArchiveError('Archive decompression budget exhausted.', 'archive_too_large');
    const compressed = bytes.subarray(start, end);
    if (entry.method === 0) {
      if (entry.compressedSize !== entry.size) throw new BrushArchiveError(`${entry.path}: stored size mismatch.`, 'archive_corrupt');
      return compressed.slice();
    }
    // A fixed output buffer means a stream that inflates past its declared
    // size fails instead of allocating without bound.
    let out;
    try {
      out = inflateSync(compressed, { out: new Uint8Array(entry.size) });
    } catch (err) {
      throw new BrushArchiveError(`${entry.path}: corrupted compressed data.`, 'archive_corrupt');
    }
    if (out.length !== entry.size) throw new BrushArchiveError(`${entry.path}: decompressed size mismatch.`, 'archive_corrupt');
    return out;
  }

  const files = entries.filter((entry) => !entry.isDirectory);
  return {
    entries: files,
    rejected: files.filter((entry) => entry.rejected),
    declaredTotal,
    find(path) {
      const wanted = String(path || '').toLowerCase();
      return files.find((entry) => entry.path.toLowerCase() === wanted) || null;
    },
    read: readEntry,
    readText(entry) {
      const data = readEntry(entry);
      return typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8').decode(data) : decodeName(data, false);
    },
  };
}
